import { TableHead, TableRow, TableCell, useTheme, Checkbox, Box, TableSortLabel } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useLocation } from 'react-router-dom';
import { getTabColorHeaderMain, TrackerDataType, trackerHeaderBlueByType } from '../../shared/constants/constants';
import {
  ContentLogKeys,
  EngagementKeys,
  IssueAndServiceSharedValues,
  IssueKeys,
  ServiceKeys,
} from '../../shared/constants/IssueAndServiceKeys';

// TODO: Add unit test to make sure they are correct subarrays from constants.js
// Also tests to make sure the same column isn't in Media and Service at some time
const wide1ColsMap = {
  MediaInteraction: [IssueKeys.campaign],
  ServiceLog: [ServiceKeys.campaign],
  EngagementLog: [EngagementKeys.campaign],
  ContentLog: [ContentLogKeys.campaign],
};

const wide2ColsMap = {
  MediaInteraction: [IssueKeys.topic],
  ServiceLog: [ServiceKeys.service],
  EngagementLog: [EngagementKeys.topic],
  ContentLog: [ContentLogKeys.topic],
};

const serviceWidthOverrides = {
  [ServiceKeys.campaign]: '15%',
  [ServiceKeys.service]: '45%',
};

const contentLogWidthOverrides = {
  [ContentLogKeys.topic]: '34%',
  [ContentLogKeys.contentType]: '16%',
};

export default function TrackerTableHeaderRow({
  columns,
  type,
  tabColor,
  isBulkEdit = false,
  isBulkEditSelectedAll = false,
  isBulkEditSelectedIndeterminate = false,
  onBulkEditSelectAll = () => {},
  sortConfig = null,
  onSort = () => {},
}) {
  const { pathname } = useLocation();
  const theme = useTheme();
  const resolvedTabColor = tabColor ?? getTabColorHeaderMain(pathname, theme);
  const headerBackgroundColor = resolvedTabColor || trackerHeaderBlueByType[TrackerDataType.SERVICE];
  const headerTextColor = '#FFFFFF';

  const getTableRowCellSx = (col) => {
    const cellSx = {
      textAlign: 'left',
      padding: '10px 6px 10px 10px',
      color: headerTextColor,
      fontFamily: "'Open Sans', sans-serif",
      fontSize: '14px',
      fontWeight: 700,
      width: 'auto',
      whiteSpace: 'nowrap',
      backgroundColor: headerBackgroundColor,
      position: 'sticky',
      top: 0,
      zIndex: 3,
    };

    if (col === IssueAndServiceSharedValues.entryDate) {
      cellSx.textAlign = 'left';
    }

    if (type === TrackerDataType.SERVICE && serviceWidthOverrides[col]) {
      cellSx.width = serviceWidthOverrides[col];
    } else if (type === TrackerDataType.CONTENT_LOG && contentLogWidthOverrides[col]) {
      cellSx.width = contentLogWidthOverrides[col];
    } else if (wide1ColsMap[type].includes(col)) {
      cellSx.width = '20%';
    } else if (wide2ColsMap[type].includes(col)) {
      cellSx.width = '40%';
    } else if (col === IssueAndServiceSharedValues.teamMembers) {
      cellSx.width = '15%';
    } else if (col === IssueAndServiceSharedValues.leadExpert) {
      cellSx.width = '10%';
    } else if (col === IssueAndServiceSharedValues.unit) {
      cellSx.width = '10%';
    } else if (
      col === IssueKeys.outlet ||
      col === EngagementKeys.organization ||
      col === ContentLogKeys.audience ||
      col === IssueAndServiceSharedValues.department
    ) {
      cellSx.width = '10%';
    }

    return cellSx;
  };

  const iconCellSx = {
    textAlign: 'left',
    padding: 0,
    color: headerTextColor,
    fontFamily: "'Open Sans', sans-serif",
    fontSize: '14px',
    fontWeight: 700,
    width: '10px',
    whiteSpace: 'nowrap',
    backgroundColor: headerBackgroundColor,
    position: 'sticky',
    top: 0,
    zIndex: 4,
    borderLeft: 'none',
    borderRight: 'none',
  };

  const sortLabelSx = {
    color: headerTextColor,
    fontFamily: "'Open Sans', sans-serif",
    '&:hover': {
      color: headerTextColor,
    },
    '&.Mui-active': {
      color: headerTextColor,
    },
    '& .MuiTableSortLabel-icon': {
      color: headerTextColor,
      opacity: 0.42,
      fontSize: '0.72rem',
      transition: 'opacity 140ms ease-in-out, color 140ms ease-in-out, transform 140ms ease-in-out',
    },
    '& .MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg)',
    },
    '& .MuiTableSortLabel-iconDirectionDesc': {
      transform: 'rotate(180deg)',
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      color: headerTextColor,
      opacity: 0.85,
    },
  };

  const getSortLabelSx = (_col) => ({
    ...sortLabelSx,
    display: 'inline-flex',
    width: '100%',
    justifyContent: 'flex-start',
    textAlign: 'left',
  });

  let tableHeaderSecondCol = <TableCell sx={iconCellSx}></TableCell>;
  if (isBulkEdit) {
    tableHeaderSecondCol = (
      <TableCell sx={iconCellSx}>
        <Box sx={{ display: 'flex', width: '48px', height: '48px', alignItems: 'center', justifyContent: 'center' }}>
          <Checkbox
            color="default"
            indeterminate={isBulkEditSelectedIndeterminate}
            checked={isBulkEditSelectedAll}
            onChange={(e) => onBulkEditSelectAll(e.target.checked)}
          />
        </Box>
      </TableCell>
    );
  }

  return (
    <TableHead sx={{ height: '60px' }}>
      <TableRow>
        <TableCell sx={iconCellSx}></TableCell>
        {tableHeaderSecondCol}
        {columns.map((col) => {
          const isActive = sortConfig?.key === col;
          const direction = isActive ? sortConfig?.direction ?? 'asc' : 'asc';
          return (
            <TableCell key={col} sx={getTableRowCellSx(col)}>
              <TableSortLabel
                active={isActive}
                direction={direction}
                onClick={() => onSort(col)}
                IconComponent={ExpandMoreRoundedIcon}
                hideSortIcon={false}
                sx={getSortLabelSx(col)}
              >
                {col}
              </TableSortLabel>
            </TableCell>
          );
        })}
      </TableRow>
    </TableHead>
  );
}
